// Astral conduit network (AD-01/03/06/08): active conduit buildings link to their
// owner's Core through a radius graph. Recomputed each tick from scratch — the node
// count is tiny (<20), so a fresh BFS is cheaper than event bookkeeping and can never
// go stale on destroy-during-tick orderings.

import { buildingDef, unitDef } from '../../content/registry';
import { dist } from '../../shared/vec';
import type { Sim } from '../sim';
import type { Building } from '../state';

const LINK_RANGE = 32; // conduit-to-conduit link distance (TEST)
const ORBIT_SOURCE = 'stable-orbit';

export function stepNetwork(sim: Sim): void {
  const st = sim.state;
  st.connectedConduits.clear();

  // any Astral player present? (network is faction-agnostic by data, gated by content)
  let anyConduits = false;
  for (const b of st.buildings.values()) {
    if (b.state === 'active' && buildingDef(b.defId).conduit) {
      anyConduits = true;
      break;
    }
  }

  // strip last tick's orbit buffs regardless (they must decay when the network breaks)
  for (const s of st.squads.values()) {
    for (let i = s.buffs.length - 1; i >= 0; i--) {
      if ((s.buffs[i] as { source?: string }).source === ORBIT_SOURCE) s.buffs.splice(i, 1);
    }
  }
  if (!anyConduits) return;

  for (const p of st.players) {
    const core = sim.coreAlive(p.id);
    if (!core) continue;
    const nodes: Building[] = [];
    for (const b of st.buildings.values()) {
      if (b.owner === p.id && b.state === 'active' && buildingDef(b.defId).conduit) nodes.push(b);
    }
    if (nodes.length === 0) continue;
    // BFS from the Core across link ranges
    const frontier: { x: number; y: number }[] = [core.pos];
    const linked = new Set<Building>();
    let grew = true;
    while (grew) {
      grew = false;
      for (const n of nodes) {
        if (linked.has(n)) continue;
        for (const f of frontier) {
          if (dist(n.pos, f) <= LINK_RANGE) {
            linked.add(n);
            frontier.push(n.pos);
            grew = true;
            break;
          }
        }
      }
    }
    for (const n of linked) st.connectedConduits.add(n.id);

    // Stable Orbit (AD-16): stationary siege squads near a linked conduit fire faster
    for (const s of st.squads.values()) {
      if (s.owner !== p.id) continue;
      const d = unitDef(s.defId);
      if (!d.roleTags.includes('siege')) continue;
      if (s.moveTarget !== null) continue; // stationary only
      for (const n of linked) {
        if (dist(s.pos, n.pos) <= 12) {
          s.buffs.push(
            Object.assign(
              { stat: 'attackSpeed' as const, mult: 1.1, remaining: 0.11 },
              { source: ORBIT_SOURCE },
            ),
          );
          break;
        }
      }
    }
  }
}

/** Is pos within reach of one of this player's linked conduits? (Emergency Translation) */
export function nearConnectedConduit(sim: Sim, player: number, pos: { x: number; y: number }, radius: number): boolean {
  for (const id of sim.state.connectedConduits) {
    const b = sim.state.buildings.get(id);
    if (b && b.owner === player && dist(b.pos, pos) <= radius) return true;
  }
  return false;
}
